// Lectura unificada de hojas de cálculo para las importaciones masivas.
//
// Acepta:
//   · .xlsx / .xls  → SheetJS (ArrayBuffer, cellDates:true).
//   · .csv / .tsv / .txt → texto delimitado. Detectamos la codificación
//     (BOM UTF-8/UTF-16, UTF-8 válido o Windows-1252) y el separador
//     (tabulador, ';' o ','), y lo parseamos con un parser RFC-4180 propio
//     para que TODOS los valores lleguen como STRING: horas "9:30", NIF o
//     teléfonos con ceros a la izquierda, "0"/"1"… no se tocan.
//
// ¿Por qué no XLSX.read(texto, { type: 'string', FS })? En 0.18.5 SheetJS
// adivina el separador por su cuenta y convierte números/fechas aunque se
// le pida raw, así que "0612345678" perdería el cero. El parser propio es
// pequeño y está cubierto por tests.
//
// Módulo puro: sin React ni DOM (salvo readSpreadsheetFile, que solo usa
// File/Blob). Toda la lógica testeable vive en parseSpreadsheetBuffer.
import * as XLSX from 'xlsx';

export const WORKBOOK_EXTENSIONS = ['xlsx', 'xls'];
export const TEXT_EXTENSIONS = ['csv', 'tsv', 'txt'];
export const ACCEPTED_EXTENSIONS = [...WORKBOOK_EXTENSIONS, ...TEXT_EXTENSIONS];
export const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(',');

const DELIMITER_LABELS = { '\t': 'tabulador', ';': 'punto y coma', ',': 'coma' };
export const delimiterLabel = (d) => DELIMITER_LABELS[d] || d;

const extOf = (fileName) => {
  const m = String(fileName || '').toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
};

// Sin instanceof: en tests (jsdom) y con Buffer de Node los constructores
// pertenecen a otro "realm" y instanceof falla aunque el objeto sea válido.
const toBytes = (input) => {
  if (input && typeof input.byteLength === 'number') {
    if (typeof input.BYTES_PER_ELEMENT === 'number' || (input.buffer && typeof input.byteOffset === 'number')) {
      return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
    }
    return new Uint8Array(input);
  }
  throw new Error('Contenido de archivo no válido.');
};

// ZIP (xlsx) empieza por "PK\x03\x04"; OLE2 (xls antiguo) por D0 CF 11 E0.
const looksLikeWorkbook = (bytes) =>
  (bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04)
  || (bytes.length >= 4 && bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0);

// ─────────────────────────────────────────────────────────────
// Codificación
// ─────────────────────────────────────────────────────────────

// Bytes 0x80–0x9F de Windows-1252 (el resto coincide con Latin-1).
const CP1252_HIGH = [
  0x20ac, 0xfffd, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021,
  0x02c6, 0x2030, 0x0160, 0x2039, 0x0152, 0xfffd, 0x017d, 0xfffd,
  0xfffd, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014,
  0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0xfffd, 0x017e, 0x0178,
];

const decodeWindows1252 = (bytes) => {
  let out = '';
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const codes = [];
    const end = Math.min(bytes.length, i + CHUNK);
    for (let j = i; j < end; j++) {
      const b = bytes[j];
      codes.push(b >= 0x80 && b <= 0x9f ? CP1252_HIGH[b - 0x80] : b);
    }
    out += String.fromCharCode(...codes);
  }
  return out;
};

const decodeUtf16 = (bytes, littleEndian) => {
  let out = '';
  const codes = [];
  for (let i = 0; i + 1 < bytes.length; i += 2) {
    codes.push(littleEndian ? bytes[i] | (bytes[i + 1] << 8) : (bytes[i] << 8) | bytes[i + 1]);
    if (codes.length >= 8192) { out += String.fromCharCode(...codes); codes.length = 0; }
  }
  return out + String.fromCharCode(...codes);
};

// Decodificador UTF-8 estricto en JS puro. Devuelve null si la secuencia no
// es UTF-8 válido. Se usa cuando el entorno no tiene TextDecoder (p. ej. el
// jsdom de los tests).
const decodeUtf8Strict = (bytes) => {
  const codes = [];
  let out = '';
  let i = 0;
  while (i < bytes.length) {
    const b = bytes[i];
    let cp;
    let n;
    if (b < 0x80) { cp = b; n = 0; }
    else if (b >= 0xc2 && b <= 0xdf) { cp = b & 0x1f; n = 1; }
    else if (b >= 0xe0 && b <= 0xef) { cp = b & 0x0f; n = 2; }
    else if (b >= 0xf0 && b <= 0xf4) { cp = b & 0x07; n = 3; }
    else return null;
    if (i + n >= bytes.length) return null; // secuencia truncada
    for (let k = 1; k <= n; k++) {
      const c = bytes[i + k];
      if (c === undefined || (c & 0xc0) !== 0x80) return null;
      cp = (cp << 6) | (c & 0x3f);
    }
    // Formas sobrelargas, sustitutos y fuera de rango → inválido.
    if ((n === 2 && cp < 0x800) || (n === 3 && (cp < 0x10000 || cp > 0x10ffff))) return null;
    if (cp >= 0xd800 && cp <= 0xdfff) return null;
    if (cp > 0xffff) {
      const v = cp - 0x10000;
      codes.push(0xd800 + (v >> 10), 0xdc00 + (v & 0x3ff));
    } else {
      codes.push(cp);
    }
    if (codes.length >= 8192) { out += String.fromCharCode(...codes); codes.length = 0; }
    i += n + 1;
  }
  return out + String.fromCharCode(...codes);
};

const decodeUtf8 = (bytes) => {
  if (typeof TextDecoder !== 'undefined') {
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch (_) {
      return null;
    }
  }
  return decodeUtf8Strict(bytes);
};

// Devuelve { text, encoding } con el BOM ya quitado.
export const decodeText = (input) => {
  const bytes = toBytes(input);
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    const text = decodeUtf8(bytes.subarray(3));
    // Con BOM UTF-8 pero bytes inválidos: mejor mostrar algo que fallar.
    return { text: text != null ? text : decodeWindows1252(bytes.subarray(3)), encoding: 'utf-8-bom' };
  }
  // Excel "Texto Unicode (*.txt)" guarda UTF-16 LE con BOM y tabuladores.
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return { text: decodeUtf16(bytes.subarray(2), true), encoding: 'utf-16le' };
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return { text: decodeUtf16(bytes.subarray(2), false), encoding: 'utf-16be' };
  }
  const utf8 = decodeUtf8(bytes);
  if (utf8 != null) return { text: utf8.replace(/^﻿/, ''), encoding: 'utf-8' };
  return { text: decodeWindows1252(bytes), encoding: 'windows-1252' };
};

// ─────────────────────────────────────────────────────────────
// Separador y parser delimitado (RFC-4180 tolerante)
// ─────────────────────────────────────────────────────────────

// Cuenta tab / ';' / ',' fuera de comillas en la primera línea no vacía
// (la de cabeceras). Empate → tab > ';' > ','. Sin ninguno → según la
// extensión (.tsv → tab, resto → ',').
export const detectDelimiter = (text, fallback = ',') => {
  const counts = { '\t': 0, ';': 0, ',': 0 };
  let inQuotes = false;
  let sawContent = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') { inQuotes = !inQuotes; sawContent = true; continue; }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (sawContent) break;
      continue;
    }
    if (!inQuotes && ch in counts) counts[ch] += 1;
    if (ch.trim()) sawContent = true;
  }
  let best = null;
  for (const d of ['\t', ';', ',']) {
    if (counts[d] > 0 && (best === null || counts[d] > counts[best])) best = d;
  }
  return best || fallback;
};

// Devuelve array de registros (array de strings). Soporta comillas con el
// separador o saltos de línea dentro, "" como comilla escapada, CRLF/LF/CR
// y un salto final. Las líneas vacías devuelven [''] (se filtran fuera).
export const parseDelimited = (text, delimiter) => {
  const records = [];
  let record = [];
  let field = '';
  let inQuotes = false;
  let fieldStarted = false; // hay algo en el campo actual (para comillas)
  const len = text.length;
  const endField = () => { record.push(field); field = ''; fieldStarted = false; };
  const endRecord = () => { endField(); records.push(record); record = []; };

  for (let i = 0; i < len; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"' && !fieldStarted) { inQuotes = true; fieldStarted = true; continue; }
    if (ch === delimiter) { endField(); continue; }
    if (ch === '\r') {
      if (text[i + 1] === '\n') i++;
      endRecord();
      continue;
    }
    if (ch === '\n') { endRecord(); continue; }
    field += ch;
    fieldStarted = true;
  }
  // Último registro si el texto no termina en salto de línea.
  if (field !== '' || fieldStarted || record.length > 0) endRecord();
  return records;
};

const isBlankRecord = (rec) => rec.every((v) => String(v).trim() === '');

// Convierte registros en objetos { cabecera: valor } al estilo de
// XLSX.utils.sheet_to_json(ws, { defval: '' }): cabeceras duplicadas →
// "X_1", "X_2"; columnas sin cabecera se descartan; filas vacías se
// omiten. Cada fila lleva __rowNum__ (no enumerable, base 0) como SheetJS.
export const recordsToRows = (records) => {
  const headerIdx = records.findIndex((r) => !isBlankRecord(r));
  if (headerIdx < 0) return { headers: [], rows: [] };
  const rawHeaders = records[headerIdx].map((h) => String(h));
  const seen = {};
  const keys = rawHeaders.map((h) => {
    const base = h.trim();
    if (!base) return null;
    if (seen[base] == null) { seen[base] = 0; return base; }
    seen[base] += 1;
    return `${base}_${seen[base]}`;
  });
  const rows = [];
  for (let r = headerIdx + 1; r < records.length; r++) {
    const rec = records[r];
    if (isBlankRecord(rec)) continue;
    const obj = {};
    keys.forEach((k, c) => {
      if (k == null) return;
      obj[k] = rec[c] != null ? rec[c] : '';
    });
    Object.defineProperty(obj, '__rowNum__', { value: r, enumerable: false });
    rows.push(obj);
  }
  return { headers: rawHeaders.filter((h) => h.trim() !== ''), rows };
};

// ─────────────────────────────────────────────────────────────
// Libro Excel
// ─────────────────────────────────────────────────────────────
const normSheet = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const pickSheet = (wb, candidates = []) => {
  for (const c of candidates) {
    const match = wb.SheetNames.find((n) => normSheet(n) === normSheet(c));
    if (match) return match;
  }
  return wb.SheetNames[0] || null;
};

const parseWorkbookBytes = (bytes, sheetCandidates) => {
  // cellDates:true → las celdas con formato Hora (h:mm) se leen como Date.
  // parseHour() tolera ambos casos, pero así es más predecible.
  const wb = XLSX.read(bytes, { type: 'array', cellDates: true });
  const sheetName = pickSheet(wb, sheetCandidates);
  if (!sheetName) return { rows: [], headers: [], sheetName: null };
  const ws = wb.Sheets[sheetName];
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false });
  const headers = (aoa[0] || []).map((h) => String(h ?? '')).filter((h) => h.trim() !== '');
  const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
  return { rows, headers, sheetName };
};

// ─────────────────────────────────────────────────────────────
// API pública
// ─────────────────────────────────────────────────────────────

// Parser puro: bytes + nombre de archivo → filas.
// Devuelve { rows, headers, sheetName, format: 'xlsx'|'csv', encoding, delimiter }.
export const parseSpreadsheetBuffer = (input, { fileName = '', sheetCandidates = [] } = {}) => {
  const bytes = toBytes(input);
  const ext = extOf(fileName);
  if (ext && !ACCEPTED_EXTENSIONS.includes(ext)) {
    throw new Error(`Formato no soportado (.${ext}). Usa .xlsx, .xls, .csv, .tsv o .txt.`);
  }
  if (bytes.length === 0) throw new Error('El archivo está vacío.');

  // La firma binaria manda (un .csv que en realidad es un xlsx renombrado
  // también se lee bien); si no, decide la extensión.
  const isWorkbook = looksLikeWorkbook(bytes) || WORKBOOK_EXTENSIONS.includes(ext);
  if (isWorkbook) {
    const { rows, headers, sheetName } = parseWorkbookBytes(bytes, sheetCandidates);
    return { rows, headers, sheetName, format: 'xlsx', encoding: null, delimiter: null };
  }

  const { text, encoding } = decodeText(bytes);
  const delimiter = detectDelimiter(text, ext === 'tsv' ? '\t' : ',');
  const { headers, rows } = recordsToRows(parseDelimited(text, delimiter));
  return { rows, headers, sheetName: null, format: 'csv', encoding, delimiter };
};

const readAsArrayBuffer = (file) => {
  if (file && typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsArrayBuffer(file);
  });
};

export const readSpreadsheetFile = async (file, opts = {}) => {
  const buffer = await readAsArrayBuffer(file);
  return parseSpreadsheetBuffer(buffer, { fileName: file?.name || '', ...opts });
};
